<template>
  <div v-editable="blok">
    <MarketingHero :blok="blok" :type="blok.post_type" />
    <div
      class="relative mx-auto grid max-w-[var(--breakpoint-2xl)] grid-cols-1 gap-8 px-6 sm:px-8 lg:grid-cols-5"
    >
      <div class="col-span-1 -mt-8 space-y-8 sm:-mt-16 lg:col-span-3">
        <LazyMarketingContentVideo
          v-if="blok.youtube_url?.url"
          :blok="videoBlok"
        />
        <LazyMarketingCardBlogInfo class="block lg:hidden" :blok="blok" />
        <div class="mx-auto max-w-prose pt-4 lg:pt-0">
          <LazyMarketingContentCopy v-if="blok.copy" :content="blok.copy" />
        </div>
        <LazyMarketingContentCarouselImage
          v-if="blok.images"
          :blok="{ images: blok.images }"
        />
      </div>
      <div class="col-span-1 space-y-8 lg:col-span-2 lg:-mt-16">
        <LazyUCard
          :ui="{
            root: 'bg-neutral-900/60 backdrop-blur-lg overflow-hidden',
            body: 'relative isolate w-full h-full !px-0',
          }"
        >
          <div
            class="absolute inset-0 h-full w-full animate-pulse opacity-20"
            style="animation-duration: 5s"
          >
            <LazyBrandTopography />
          </div>
          <LazyUBadge
            class="absolute top-3 right-3 z-[3] rounded-full bg-neutral-800 p-2 backdrop-blur-lg hover:bg-neutral-800/80"
          >
            <UIcon name="i-lucide-mail" class="size-5 text-yellow-400" />
          </LazyUBadge>
          <div
            class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/50 to-neutral-950"
          />
          <div class="relative z-[2] mx-auto max-w-prose px-6 sm:px-8">
            <div class="mb-6 sm:mb-8">
              <div class="mb-0.5 text-base font-bold text-yellow-400 uppercase">
                Newsletter
              </div>
              <h2 class="text-3xl font-extrabold text-white">
                Join The Adventure Almanac
              </h2>
              <p class="mt-1 text-lg text-neutral-400 *:font-medium">
                Get our latest stories, competitions, events & offers, straight
                to your inbox.
              </p>
            </div>
            <LazyMarketingFormNewsletter />
          </div>
        </LazyUCard>
        <LazyMarketingCardBlogInfo class="hidden lg:block" :blok="blok" />
        <div class="relative mt-8 grid grid-cols-2 gap-4">
          <LazyMarketingCardBlog
            v-for="(article, index) in blog"
            v-if="!loading && !isLoading && blog.length > 0"
            :key="index"
            :article="article"
            class="col-span-2 sm:col-span-1 lg:col-span-2 xl:col-span-1"
          />
          <LazyMarketingCardBlogLoading
            v-for="i in 4"
            v-if="loading || isLoading"
            :key="i"
            class="col-span-2 sm:col-span-1 lg:col-span-2 xl:col-span-1"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  BlogStoryblok,
  MultilinkStoryblok,
} from '~/types/component-types-sb';

const isPreview = useRuntimeConfig().public.NODE_ENV !== 'production';
const props = defineProps<{
  blok: BlogStoryblok;
  uuid?: string;
  name?: string;
}>();

const { fetchBlog, isLoading } = useStoryblokClient();

const blog = ref<BlogStoryblok[]>([]);
const loading = ref(true);

// Add computed property for date check
const shouldRedirect = computed(() => {
  if (!props.blok.date || isPreview) return false;
  const blokDate = new Date(props.blok.date);
  const currentDate = new Date();
  return blokDate > currentDate;
});

const videoBlok = computed(() => ({
  youtube_url: props.blok.youtube_url as Exclude<
    MultilinkStoryblok,
    { linktype?: 'email' } | { linktype?: 'asset' }
  >,
}));

// Load blog and set them in the reactive `blog` array
const loadBlog = async (page: number, perPage: number) => {
  loading.value = true;
  try {
    const stories = await fetchBlog(page, perPage);
    blog.value = stories;
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};

// Initial load with hydration guard
onMounted(async () => {
  if (import.meta.client) {
    if (shouldRedirect.value) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page could not be found.',
      });
    }
    await loadBlog(1, 4);
  }
});
</script>
